import { Injectable } from '@angular/core';
import { Memoize } from '@ddt-web/generic-utils';

@Injectable({
	providedIn: 'root'
})
export class PortfolioIdUtil {

	private static UUID4_REGEX = new RegExp(/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/);
	private static PARTNER_PREFIX = 'PARTNER@';
	private static THIRD_PARTY_MODEL_PREFIX = 'TPM@';
	public static ALADDIN_PORTFOLIO_CODE_PREFIX = 'APC@';
	public static EXTERNAL_PARTNER_PORTFOLIO_PREFIX = 'EXT@';

	/**
	 * Is a partner ID.
	 *
	 * @param portfolioId the portfolio ID to test.
	 * @return true if valid, false otherwise.
	 */
	@Memoize()
	public static isPartner(portfolioId: string): boolean {
		return !!portfolioId && String(portfolioId).startsWith(this.PARTNER_PREFIX);
	}

	/**
	 * Is a model - BLK or third party
	 *
	 * @param portfolioId the portfolio ID to test.
	 * @return true if valid, false otherwise.
	 */
	@Memoize()
	public static isModel(portfolioId: string | number): boolean {
		return this.isBlackRockModel(portfolioId) || this.isThirdPartyModel(String(portfolioId));
	}

	/**
	 * Is a third party model ID.
	 *
	 * @param portfolioId the portfolio ID to test.
	 * @return true if valid, false otherwise.
	 */
	@Memoize()
	public static isThirdPartyModel(portfolioId: string): boolean {
		return !!portfolioId && String(portfolioId).startsWith(this.THIRD_PARTY_MODEL_PREFIX);
	}

	/**
	 * Is a BlackRock model ID.
	 *
	 * @param portfolioId the portfolio ID to test.
	 * @return true if valid, false otherwise.
	 */
	@Memoize()
	public static isBlackRockModel(portfolioId: string | number): boolean {
		if (!portfolioId) {
			return false;
		}

		// Match model ids containing 6 to 9 digits (only digits, no other characters)
		const modelRegex = /^\d{6,9}$/;
		const modelId = String(portfolioId);
		return modelRegex.test(modelId);
	}

	/**
	 * Is a user portfolio ID.
	 *
	 * @param portfolioId the portfolio ID to test.
	 * @return true if valid, false otherwise.
	 */
	@Memoize()
	public static isUserPortfolio(portfolioId: string): boolean {
		return !!portfolioId && this.UUID4_REGEX.test(String(portfolioId));
	}

	/**
	 * Is a aladdin portfolio code.
	 *
	 * @param portfolioId the portfolio ID to test.
	 * @return true if valid, false otherwise.
	 */
	@Memoize()
	public static isAladdinPortfolioCode(portfolioId: string): boolean {
		return !!portfolioId && String(portfolioId).startsWith(this.ALADDIN_PORTFOLIO_CODE_PREFIX);
	}

	/**
	 * Get aladdin portfolio code from format `APC@159920` which would just be `159920`.
	 *
	 * @param id the ID to convert.
	 * @return id
	 */
	@Memoize()
	public static getAladdinPortfolioCode(id: string): string {
		return id && (this.isAladdinPortfolioCode(id) ? id.split('@').pop() : id);
	}

	/**
	 * Is a external partner portfolio.
	 *
	 * @param extPortfolioId the external portfolio ID to test.
	 * @return true if valid, false otherwise.
	 */
	@Memoize()
	public static isExternalPortfolio(extPortfolioId: string): boolean {
		return !!extPortfolioId && extPortfolioId.startsWith(this.EXTERNAL_PARTNER_PORTFOLIO_PREFIX);
	}
}
